/**
 * Sichtbare "Bot"-Markierung für Bot-Accounts (Migration 0085).
 *
 * Bots stehen bewusst in derselben Benutzerliste wie Menschen — sie sind
 * Autor von Kommentaren/Notizen, Ersteller von Aufgaben, Assignee und im
 * Mention-Picker wählbar. Überall dort muss erkennbar bleiben, dass kein
 * Mensch dahinter steht.
 *
 * Konventionen (bewusst EINE für die ganze App):
 * - Farbe: tertiary-container — klar unterscheidbar vom KI-Badge
 *   (`AiOriginBadge`, primary-container), dem stilistischen Vorbild.
 * - Label: immer "Bot", plus Tooltip "handelt im Auftrag von <Owner>".
 * - Auflösung des Owners über denselben Users-Store (`findUser`).
 *
 * Wo kein JSX möglich ist (native `<option>`), gibt `botSuffix()` denselben
 * Hinweis als reinen Text zurück.
 */

import { Bot } from "lucide-preact";
import { findUser } from "../store/users";

type Size = "sm" | "md";

const ICON_PX: Record<Size, number> = { sm: 11, md: 13 };
const TEXT_CLS: Record<Size, string> = { sm: "text-label", md: "text-micro" };

/** Tooltip-Text: mit Owner-Name, sofern auflösbar. */
export function botTitle(ownerId?: number | null): string {
  const owner = findUser(ownerId);
  return owner
    ? `Bot — handelt im Auftrag von ${owner.name}`
    : "Bot — automatisierter Account";
}

/**
 * Tooltip für Avatar-artige Kontexte ohne sichtbares Label: hängt bei Bots
 * den Bot-Hinweis samt Owner an den Anzeigenamen an, bei Menschen bleibt es
 * beim Namen.
 */
export function userTitle(
  userId: number | null | undefined,
  displayName: string
): string {
  const user = findUser(userId);
  if (!user?.is_bot) return displayName;
  return `${displayName} — ${botTitle(user.bot_owner_id)}`;
}

/** True, wenn die Id zu einem Bot-Account gehört. */
export function isBotUser(userId: number | null | undefined): boolean {
  return findUser(userId)?.is_bot === true;
}

/**
 * Text-Suffix " (Bot)" für Kontexte ohne JSX (HTML-Strings, native
 * `<option>`), in denen nur die Id vorliegt. Liegt das User-Objekt selbst vor,
 * prüft der Aufrufer direkt `u.is_bot` — das kommt ohne Store-Lookup aus.
 */
export function botSuffix(userId: number | null | undefined): string {
  return isBotUser(userId) ? " (Bot)" : "";
}

interface Props {
  /** Owner, in dessen Auftrag der Bot handelt (nur für den Tooltip). */
  ownerId?: number | null;
  size?: Size;
  className?: string;
}

/** Das Badge selbst — rendert immer, der Aufrufer entscheidet über das Ob. */
export function BotBadge({ ownerId, size = "sm", className }: Props): preact.JSX.Element {
  const title = botTitle(ownerId);
  return (
    <span
      class={`inline-flex items-center gap-0.5 rounded-sm bg-[color:var(--color-tertiary-container)] px-1 py-0.5 ${TEXT_CLS[size]} font-medium text-[color:var(--color-on-tertiary-container)] shrink-0 leading-none ${
        className ?? ""
      }`}
      title={title}
      aria-label={title}
    >
      <Bot size={ICON_PX[size]} aria-hidden="true" />
      Bot
    </span>
  );
}

/**
 * Quelle der Bot-Eigenschaft für die id-basierten Helfer.
 *
 * Der Users-Store speist sich aus GET /api/users und enthält nur AKTIVE
 * Accounts. Ein Bot wird beim „Löschen" nur deaktiviert — seine users-Zeile
 * bleibt wegen der Autorschaft bestehen, aus dem Store verschwindet er aber.
 * Über den Store aufgelöst verlören seine alten Beiträge damit rückwirkend die
 * Kennzeichnung. Deshalb: liegt `isBot` am Payload vor (Autor-DTOs liefern es
 * mit), entscheidet dieser Wert; der Store ist nur der Fallback für Kontexte,
 * die ausschliesslich eine Id haben.
 */
interface ForProps {
  /** Benutzer-Id — Fallback-Auflösung, wenn `isBot` nicht übergeben wird. */
  userId?: number | null;
  /** Explizit aus dem Payload; hat Vorrang vor dem Store. */
  isBot?: boolean;
  /** Owner aus dem Payload; nur zusammen mit `isBot` ausgewertet. */
  ownerId?: number | null;
  size?: Size;
  className?: string;
}

/** Bot-Eigenschaft + Owner ermitteln: Payload schlägt Store. */
export function resolveBot(props: {
  userId?: number | null;
  isBot?: boolean;
  ownerId?: number | null;
}): { isBot: boolean; ownerId: number | null } {
  if (props.isBot !== undefined) {
    return { isBot: props.isBot, ownerId: props.ownerId ?? null };
  }
  const user = findUser(props.userId);
  return { isBot: user?.is_bot === true, ownerId: user?.bot_owner_id ?? null };
}

/**
 * Bequemer Aufruf an Stellen, die nur eine Benutzer-Id oder ein Autor-DTO
 * haben: rendert das Badge für Bots und `null` für Menschen.
 */
export function BotBadgeFor({
  userId,
  isBot,
  ownerId,
  size,
  className,
}: ForProps): preact.JSX.Element | null {
  const bot = resolveBot({ userId, isBot, ownerId });
  if (!bot.isBot) return null;
  return <BotBadge ownerId={bot.ownerId} size={size} className={className} />;
}

/**
 * Zweitzeile "im Auftrag von <Owner>" — für Autor-Kontexte (Kommentare,
 * Notizen), wo der Auftraggeber die eigentlich interessante Information ist.
 * Rendert `null` für Menschen und für Bots ohne auflösbaren Owner. Den
 * Owner-Namen liefert weiterhin der Store: der Owner ist ein Mensch und im
 * Regelfall aktiv.
 */
export function BotOwnerNote({
  userId,
  isBot,
  ownerId,
  className,
}: {
  userId?: number | null;
  isBot?: boolean;
  ownerId?: number | null;
  className?: string;
}): preact.JSX.Element | null {
  const bot = resolveBot({ userId, isBot, ownerId });
  if (!bot.isBot) return null;
  const owner = findUser(bot.ownerId);
  if (!owner) return null;
  return (
    <p
      class={`text-label text-[color:var(--color-on-surface-variant)] ${className ?? ""}`}
    >
      im Auftrag von {owner.name}
    </p>
  );
}
